import { useState } from "react";
import { Icon } from "./Icons";
import { actions, STUDIO } from "../lib/store";
import { relative } from "../lib/format";
import { Presenter } from "../studio/ConceptDeck";
import { FilmFrame } from "./FilmFrame";
import { SheetViewer, type Sheet } from "./SheetViewer";
import { AcceptDeck } from "./AcceptDeck";
import type { Project, DesignOption } from "../lib/types";

/* The client's side of the concept: read the thinking, compare the options
   honestly side by side, and say which one you want — with your reasons
   attached, so nobody has to reconstruct them from a phone call later. */

export function ConceptReview({ p, who }: { p: Project; who: string }) {
  const deck = p.deck;
  const [present, setPresent] = useState<number | null>(null);
  const [compare, setCompare] = useState(false);
  /* Every hook has to run on every render — this one sits above the early
     return below, not with the derived values further down. */
  const [sheetAt, setSheetAt] = useState<number | null>(null);
  const [extra, setExtra] = useState<Sheet | null>(null);

  const past = (p.deckArchive ?? []).filter((d) => d.status === "issued");
  if (!deck || deck.status !== "issued") {
    return (
      <div className="reveal">
        <h2 className="serif" style={{ fontSize: 30, marginBottom: 8 }}>The concept</h2>
        <p className="muted" style={{ maxWidth: 560, lineHeight: 1.7 }}>
          {past.length
            ? `${STUDIO.name} is working on the next presentation. What you were shown before is below — it stays here.`
            : `${STUDIO.name} is working on the design concept. When it's ready you'll find the full presentation here, along with the options to choose between.`}
        </p>
        {past.length > 0 && <EarlierDecks p={p} />}
      </div>
    );
  }

  const chosen = deck.options.find((o) => o.response?.choice === "preferred");
  const answered = deck.options.filter((o) => o.response).length;
  /* One list of sheets for the whole page, so opening any board lets the client
     page through all of them without coming back out. */
  const sheets: Sheet[] = deck.options
    .filter((o) => o.images[0])
    .map((o) => ({ ref: o.boardRef ?? `Option ${o.ref}`, name: o.name, url: o.images[0] }))
    .filter((x, i, a) => a.findIndex((y) => y.url === x.url) === i);
  /* A slide's snip isn't one of the full boards, so opening it must show THAT
     image, not fall back to the first sheet in the list. */
  const openSheet = (url: string) => {
    const at = sheets.findIndex((x) => x.url === url);
    if (at >= 0) { setExtra(null); setSheetAt(at); return; }
    setExtra({ ref: "From the presentation", url });
    setSheetAt(0);
  };
  const viewing: Sheet[] = extra ? [extra] : sheets;
  const filmSlide = deck.slides.find((s) => s.kind === "film");
  const openingFilm = filmSlide?.filmId ? (p.films ?? []).find((f) => f.id === filmSlide.filmId) : undefined;

  if (deck.kind === "renders") return <RenderReview p={p} who={who} past={past} />;

  return (
    <div className="reveal">
      <div className="row between wrap" style={{ gap: 14, marginBottom: 8 }}>
        <div style={{ minWidth: 0 }}>
          <h2 className="serif" style={{ fontSize: 30 }}>{deck.title}</h2>
          {deck.subtitle && <p className="muted" style={{ marginTop: 4 }}>{deck.subtitle}</p>}
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          {deck.options.length > 1 && <button className="btn ghost sm" onClick={() => setCompare((v) => !v)}><Icon.layers width={14} />{compare ? "Read as cards" : "Compare side by side"}</button>}
          <button className="btn accent sm" onClick={() => setPresent(0)}><Icon.present width={14} />View the presentation</button>
        </div>
      </div>
      <div className="dim" style={{ fontSize: 12.5, marginBottom: 22 }}>
        Presented {deck.issuedOn ? relative(deck.issuedOn) : "recently"} · {deck.slides.length} slide{deck.slides.length === 1 ? "" : "s"}
      </div>

      {/* The film comes first. It's the shortest route into the thinking, and the
          drawings read differently once you've watched the idea move. */}
      {openingFilm && (
        <div style={{ marginBottom: 30 }}>
          <FilmFrame film={openingFilm} height="clamp(380px, 66vh, 720px)" />
        </div>
      )}

      {/* what we need from you */}
      {deck.options.length > 0 && (
        <div className="card pad reveal" style={{ marginBottom: 22, borderLeft: `3px solid ${chosen ? "var(--ok)" : "var(--accent)"}` }}>
          {chosen ? (
            <>
              <div className="row" style={{ gap: 9 }}><Icon.check width={16} style={{ color: "var(--ok)" }} /><b style={{ fontSize: 14.5 }}>You've chosen {chosen.name}</b></div>
              <p className="muted" style={{ fontSize: 13.5, marginTop: 7, lineHeight: 1.65 }}>
                {STUDIO.name} will develop this direction. You can change your mind below while we're still at concept
                stage — once the design is being drawn up, changing direction costs time and money, so tell us now if
                you're unsure.
              </p>
            </>
          ) : (
            <>
              <div className="row" style={{ gap: 9 }}><Icon.spark width={16} style={{ color: "var(--accent-ink)" }} /><b style={{ fontSize: 14.5 }}>Which direction should we take?</b></div>
              <p className="muted" style={{ fontSize: 13.5, marginTop: 7, lineHeight: 1.65 }}>
                Read through the {deck.options.length} options below. Each one shows what it does well and what it gives
                up — there's no option without a trade-off. Mark the one you'd like us to develop, and tell us why in
                your own words; it's the "why" that shapes the next stage.
              </p>
              {answered > 0 && <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>{answered} of {deck.options.length} responded to — none marked as preferred yet.</div>}
            </>
          )}
        </div>
      )}

      {/* the options */}
      {deck.options.length > 0 && (
        compare
          ? <CompareTable p={p} who={who} />
          : (
            <div className="col" style={{ gap: 16 }}>
              {deck.options.map((o) => <OptionCard key={o.id} p={p} o={o} who={who} onOpenSheet={openSheet} />)}
            </div>
          )
      )}

      {/* the narrative, readable inline */}
      <div style={{ marginTop: 30 }}>
        <div className="kicker" style={{ marginBottom: 12 }}>The thinking behind it</div>
        <div className="col" style={{ gap: 14 }}>
          {deck.slides.filter((s) => s.kind !== "cover" && s.kind !== "film").map((s) => (
            <div key={s.id} className="card pad reveal">
              <div className="row between wrap" style={{ gap: 10 }}>
                <h3 className="serif" style={{ fontSize: 20 }}>{s.title}</h3>
                <button className="chip" style={{ fontSize: 11 }} onClick={() => setPresent(deck.slides.findIndex((x) => x.id === s.id))}>Full screen</button>
              </div>
              {s.body && <p style={{ fontSize: 14.5, lineHeight: 1.75, marginTop: 9, whiteSpace: "pre-wrap", color: "var(--ink-2)" }}>{s.body}</p>}
              {s.video && (
                <video src={s.video} controls playsInline preload="metadata"
                  style={{ width: "100%", marginTop: 12, borderRadius: 8, background: "#000", display: "block" }} />
              )}
              {(s.images?.length ?? 0) > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit,minmax(${s.images!.length > 1 ? 180 : 320}px,1fr))`, gap: 8, marginTop: 12 }}>
                  {/* these are snipped drawings — show them whole, and let a
                      click open them properly rather than squinting */}
                  {s.images!.map((src, k) => (
                    <button key={k} onClick={() => openSheet(src)}
                      style={{ padding: 0, border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", background: "#fff", cursor: "zoom-in", lineHeight: 0 }}>
                      <img src={src} alt="" style={{ width: "100%", maxHeight: 340, objectFit: "contain", display: "block" }} />
                    </button>
                  ))}
                </div>
              )}
              <SlideComment p={p} slideId={s.id} who={who} />
            </div>
          ))}
        </div>
      </div>

      <AcceptDeck p={p} who={who} />

      <FullSet p={p} onOpen={openSheet} />
      {past.length > 0 && <EarlierDecks p={p} />}

      {sheetAt !== null && <SheetViewer sheets={viewing} start={sheetAt} onClose={() => setSheetAt(null)} />}
      {present !== null && <Presenter p={p} start={present} onClose={() => setPresent(null)} />}
    </div>
  );
}


/* ---- a render presentation ----
   Images at full width, one per view, each with a place to say how it feels.
   No options, no compare table, no argument — the picture is the argument. */
function RenderReview({ p, who, past }: { p: Project; who: string; past: any[] }) {
  const deck = p.deck!;
  const [at, setAt] = useState<number | null>(null);
  const views = deck.slides.filter((s) => s.kind !== "cover" && ((s.images?.length ?? 0) > 0 || s.video));
  /* Every view, in order — so the full-screen arrows walk the whole set. */
  const gallery: Sheet[] = views.filter((s) => !s.video && s.images?.[0]).map((s) => ({ ref: s.title || "View", url: s.images![0] }));
  const onOpen = (url: string) => setAt(Math.max(0, gallery.findIndex((g) => g.url === url)));
  const words = deck.slides.filter((s) => s.kind !== "cover" && !(s.images?.length ?? 0) && s.body);

  return (
    <div className="reveal">
      <h2 className="serif" style={{ fontSize: 30 }}>{deck.title}</h2>
      {deck.subtitle && <p className="muted" style={{ marginTop: 4 }}>{deck.subtitle}</p>}
      <div className="dim" style={{ fontSize: 12.5, marginTop: 6, marginBottom: 24 }}>
        {views.length} view{views.length === 1 ? "" : "s"} · presented {deck.issuedOn ? relative(deck.issuedOn) : "recently"}
        {" · click any image to see it full size"}
      </div>

      {words.map((s) => (
        <p key={s.id} className="muted" style={{ fontSize: 14.5, lineHeight: 1.75, maxWidth: 680, marginBottom: 20 }}>{s.body}</p>
      ))}

      <div className="col" style={{ gap: 34 }}>
        {views.map((s) => (
          <div key={s.id}>
            {s.video ? (
              /* Full-quality video, played where it sits — no download, no new tab. */
              <video src={s.video} controls playsInline preload="metadata"
                style={{ width: "100%", display: "block", borderRadius: 10, background: "#000" }} />
            ) : (
              <button onClick={() => onOpen(s.images![0])}
                style={{ display: "block", width: "100%", padding: 0, border: "none", background: "#111", borderRadius: 10, overflow: "hidden", cursor: "zoom-in", lineHeight: 0 }}>
                <img src={s.images![0]} alt={s.title} style={{ width: "100%", display: "block" }} />
              </button>
            )}
            <div className="row between wrap" style={{ gap: 10, marginTop: 10 }}>
              <div style={{ minWidth: 0 }}>
                <h3 className="serif" style={{ fontSize: 19 }}>{s.title}</h3>
                {s.body && <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.65, marginTop: 3, maxWidth: 620 }}>{s.body}</p>}
              </div>
            </div>
            <SlideComment p={p} slideId={s.id} who={who} />
          </div>
        ))}
      </div>

      {views.length === 0 && (
        <p className="muted" style={{ fontSize: 14, lineHeight: 1.7 }}>
          The views are being prepared — they'll appear here as soon as they're ready.
        </p>
      )}

      <AcceptDeck p={p} who={who} />

      {past.length > 0 && <EarlierDecks p={p} />}
      {at !== null && <SheetViewer sheets={gallery} start={at} onClose={() => setAt(null)} />}
    </div>
  );
}

/* ---- what they were shown before ----
   A project is presented at each stage. Closing one doesn't take it away from
   the client — they can still read it and what they said at the time. */
function EarlierDecks({ p }: { p: Project }) {
  const past = (p.deckArchive ?? []).filter((d) => d.status === "issued");
  /* An archived deck plays through the same full-screen presenter as the live
     one — the client can always go back to exactly what they were shown. The
     shimmed project swaps only the deck; the presenter reads nothing else. */
  const [viewing, setViewing] = useState<string | null>(null);
  if (!past.length) return null;
  const open = past.find((d) => d.id === viewing);
  return (
    <div style={{ marginTop: 34 }}>
      <div className="kicker" style={{ marginBottom: 10 }}>Earlier presentations — kept for the record</div>
      <div className="col" style={{ gap: 10 }}>
        {past.map((d) => {
          const chosen = d.options.find((o) => o.response?.choice === "preferred");
          return (
            <div key={d.id} className="card pad">
              <div className="row between wrap" style={{ gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="row wrap" style={{ gap: 7, alignItems: "baseline" }}>
                    {d.stage && <span className="badge" style={{ fontSize: 8.5 }}>{d.stage}</span>}
                    <h3 className="serif" style={{ fontSize: 18 }}>{d.title}</h3>
                  </div>
                  <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>
                    Presented {d.issuedOn ? relative(d.issuedOn) : "earlier"}
                    {chosen ? ` · you chose ${chosen.name}` : ""}
                    {` · ${d.slides.length} slide${d.slides.length === 1 ? "" : "s"}`}
                  </div>
                </div>
                <button className="btn soft sm" onClick={() => setViewing(d.id)}><Icon.present width={13} />View in full</button>
              </div>
              {d.slides.filter((s) => s.kind !== "cover" && s.kind !== "film").slice(0, 3).map((s) => (
                <div key={s.id} style={{ marginTop: 10 }}>
                  <b style={{ fontSize: 13.5 }}>{s.title}</b>
                  {s.body && <p className="muted" style={{ fontSize: 13, lineHeight: 1.65, marginTop: 3 }}>{s.body}</p>}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {open && <Presenter p={{ ...p, deck: open }} start={0} onClose={() => setViewing(null)} />}
    </div>
  );
}

/* ---- the sheets behind the presentation ----
   The presentation makes the case; the boards are where a client goes to check
   it. Every sheet the options came from, full size, plus the way through to the
   whole drawing set when there is one. */
function FullSet({ p, onOpen }: { p: Project; onOpen: (url: string) => void }) {
  const deck = p.deck!;
  const boards = deck.options
    .filter((o) => o.images[0] && (o.boardRef || o.page))
    .map((o) => ({ ref: o.boardRef ?? `Option ${o.ref}`, name: o.name, url: o.images[0] }));
  const seen = new Set<string>();
  const unique = boards.filter((b) => (seen.has(b.url) ? false : (seen.add(b.url), true)));
  const hasSet = (p.deliverables ?? []).some((d: any) => d.previewUrl || d.fileUrl);
  if (!unique.length && !hasSet) return null;

  return (
    <div style={{ marginTop: 34 }}>
      <div className="row between wrap" style={{ gap: 10, marginBottom: 6 }}>
        <div className="kicker">The sheets behind it</div>
        {hasSet && (
          <a className="btn ghost sm" href={`/portal/${p.id}/drawings`}><Icon.layers width={13} />The full drawing set</a>
        )}
      </div>
      <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.65, maxWidth: 640, marginBottom: 14 }}>
        The presentation makes the case; these are the drawings it was made from. Open any sheet full size to read the
        plans, the parking layouts and the printed figures for yourself.
      </p>
      {unique.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
          {unique.map((b, i) => (
            <button key={i} onClick={() => onOpen(b.url)} className="card"
              style={{ display: "block", overflow: "hidden", textAlign: "left", padding: 0, border: "1px solid var(--line)", background: "var(--card)", color: "inherit", cursor: "zoom-in" }}>
              <img src={b.url} alt={b.ref} style={{ width: "100%", background: "#fff", display: "block", aspectRatio: "4 / 3", objectFit: "contain" }} />
              <div style={{ padding: "9px 12px" }}>
                <div className="mono" style={{ fontSize: 11.5 }}>{b.ref}</div>
                <div className="dim" style={{ fontSize: 12, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.name}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- one option, in full ---- */
function OptionCard({ p, o, who, onOpenSheet }: { p: Project; o: DesignOption; who: string; onOpenSheet?: (url: string) => void }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState(o.response?.comment ?? "");
  const preferred = o.response?.choice === "preferred";
  const isBoard = !!(o.boardRef || o.page);

  function respond(choice: "preferred" | "considering" | "no") {
    actions.respondToOption(p.id, o.id, choice, who, comment.trim() || undefined);
    setOpen(false);
  }

  return (
    <div className="card" style={{ overflow: "hidden", borderColor: preferred ? "var(--ok)" : "var(--line)", borderWidth: preferred ? 2 : 1 }}>
      {o.images.length > 0 && (
        /* A board is a drawing: show it whole. Cropping a plan to fill a box
           throws away the half the client needed to see. */
        isBoard
          ? (
            <button onClick={() => onOpenSheet?.(o.images[0])} title="Open the board full size"
              style={{ display: "block", width: "100%", padding: 0, border: "none", background: "#fff", borderBottom: "1px solid var(--line)", cursor: onOpenSheet ? "zoom-in" : "default" }}>
              <img src={o.images[0]} alt={`Board ${o.boardRef ?? o.ref}`}
                style={{ width: "100%", maxHeight: "min(58vh, 560px)", objectFit: "contain", display: "block" }} />
            </button>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(o.images.length, 3)},1fr)`, gap: 2 }}>
              {o.images.slice(0, 3).map((src, k) => <img key={k} src={src} alt="" style={{ width: "100%", height: o.images.length > 1 ? 150 : 240, objectFit: "cover", display: "block" }} />)}
            </div>
          )
      )}
      <div style={{ padding: "16px 20px 18px" }}>
        <div className="row between wrap" style={{ gap: 10 }}>
          <span className="row" style={{ gap: 9, alignItems: "baseline", minWidth: 0 }}>
            <span className="badge accent mono" style={{ fontSize: 10 }}>Option {o.ref}</span>
            <h3 className="serif" style={{ fontSize: 22 }}>{o.name}</h3>
          </span>
          {o.response && (
            <span className="badge" style={{ fontSize: 9, color: preferred ? "var(--ok)" : o.response.choice === "no" ? "var(--danger)" : "var(--warn)" }}>
              {preferred ? "Your choice" : o.response.choice === "no" ? "Not this one" : "Considering"}
            </span>
          )}
        </div>
        {o.boardRef && <div className="dim mono" style={{ fontSize: 11, marginTop: 4 }}>Sheet {o.boardRef}</div>}
        {o.thesis && <p style={{ fontSize: 15, fontStyle: "italic", color: "var(--ink-2)", marginTop: 8, lineHeight: 1.6 }}>{o.thesis}</p>}
        {o.description && <p style={{ fontSize: 14, lineHeight: 1.75, marginTop: 10, whiteSpace: "pre-wrap" }}>{o.description}</p>}

        {/* the figures printed on the sheet */}
        {((o.metrics ?? []).length > 0 || o.area) && (
          <div className="row wrap" style={{ gap: 10, marginTop: 14 }}>
            {o.area ? (
              <div className="panel" style={{ padding: "7px 12px", minWidth: 120 }}>
                <div className="kicker" style={{ fontSize: 8 }}>Area</div>
                <div className="mono" style={{ fontSize: 14, marginTop: 2 }}>{o.area.toLocaleString()} {p.giaUnit === "sqm" ? "m²" : "ft²"}</div>
              </div>
            ) : null}
            {(o.metrics ?? []).map((m, k) => (
              <div key={k} className="panel" style={{ padding: "7px 12px", minWidth: 120 }}>
                <div className="kicker" style={{ fontSize: 8 }}>{m.label}</div>
                <div className="mono" style={{ fontSize: 14, marginTop: 2 }}>{m.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* the narration: what was decided, and what it achieves */}
        {(o.decisions ?? []).length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="kicker" style={{ fontSize: 9 }}>The decisions behind it</div>
            <ol style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 14, lineHeight: 1.75 }}>
              {o.decisions!.map((d, k) => <li key={k} style={{ marginBottom: 4 }}>{d}</li>)}
            </ol>
          </div>
        )}

        <div className="row wrap" style={{ gap: 18, marginTop: 16, alignItems: "flex-start" }}>
          {o.strengths.length > 0 && (
            <div style={{ flex: 1, minWidth: 210 }}>
              <div className="kicker" style={{ fontSize: 9, color: "var(--ok)" }}>What it gives you</div>
              <ul style={{ margin: "7px 0 0", paddingLeft: 17, fontSize: 13.5, lineHeight: 1.7, color: "var(--ink-2)" }}>{o.strengths.map((s, k) => <li key={k}>{s}</li>)}</ul>
            </div>
          )}
          {o.tradeoffs.length > 0 && (
            <div style={{ flex: 1, minWidth: 210 }}>
              <div className="kicker" style={{ fontSize: 9, color: "var(--warn)" }}>What it gives up</div>
              <ul style={{ margin: "7px 0 0", paddingLeft: 17, fontSize: 13.5, lineHeight: 1.7, color: "var(--ink-2)" }}>{o.tradeoffs.map((s, k) => <li key={k}>{s}</li>)}</ul>
            </div>
          )}
        </div>
        {(o.costNote || o.area) && (
          <div className="row wrap" style={{ gap: 14, marginTop: 14, fontSize: 12.5, color: "var(--ink-3)" }}>
            {o.area ? <span>Approx. {o.area.toLocaleString()} {p.giaUnit === "sqm" ? "m²" : "ft²"}</span> : null}
            {o.costNote ? <span>{o.costNote}</span> : null}
          </div>
        )}

        {o.response?.comment && !open && (
          <div className="panel" style={{ padding: "9px 12px", marginTop: 14 }}>
            <div className="kicker" style={{ fontSize: 8 }}>What you said</div>
            <div style={{ fontSize: 13, marginTop: 3, lineHeight: 1.55 }}>{o.response.comment}</div>
          </div>
        )}

        {open ? (
          <div className="panel" style={{ padding: "13px 15px", marginTop: 15 }}>
            <div className="field"><label>Anything you'd want changed about this option?</label>
              <textarea className="input" rows={3} autoFocus value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="e.g. I like this one but the kitchen feels closed off from the terrace." />
            </div>
            <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
              <button className="btn accent sm" onClick={() => respond("preferred")}><Icon.check width={14} />This is the one</button>
              <button className="btn soft sm" onClick={() => respond("considering")}>Still considering</button>
              <button className="btn ghost sm" onClick={() => respond("no")}>Not this one</button>
              <span style={{ flex: 1 }} />
              <button className="btn ghost sm" onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="row wrap" style={{ gap: 8, marginTop: 16 }}>
            {preferred
              ? <button className="btn ghost sm" onClick={() => setOpen(true)}>Change my response</button>
              : <button className="btn accent sm" onClick={() => setOpen(true)}><Icon.check width={14} />Respond to this option</button>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- side-by-side comparison ---- */
function CompareTable({ p, who }: { p: Project; who: string }) {
  const deck = p.deck!;
  const os = deck.options;
  const unit = p.giaUnit === "sqm" ? "m²" : "ft²";
  const rows: { label: string; get: (o: DesignOption) => React.ReactNode }[] = [
    { label: "The idea", get: (o) => o.thesis || <span className="dim">—</span> },
    { label: "What it gives you", get: (o) => o.strengths.length ? <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.65 }}>{o.strengths.map((s, k) => <li key={k}>{s}</li>)}</ul> : <span className="dim">—</span> },
    { label: "What it gives up", get: (o) => o.tradeoffs.length ? <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.65 }}>{o.tradeoffs.map((s, k) => <li key={k}>{s}</li>)}</ul> : <span className="dim">—</span> },
    { label: "Approx. area", get: (o) => o.area ? `${o.area.toLocaleString()} ${unit}` : <span className="dim">—</span> },
    { label: "Cost", get: (o) => o.costNote || <span className="dim">—</span> },
  ];

  return (
    <div className="card" style={{ overflowX: "auto" }}>
      <table className="table" style={{ minWidth: 120 + os.length * 230 }}>
        <thead>
          <tr>
            <th style={{ width: 130 }} />
            {os.map((o) => (
              <th key={o.id} style={{ minWidth: 220, verticalAlign: "top", background: o.response?.choice === "preferred" ? "var(--ok-bg,rgba(90,150,110,.08))" : undefined }}>
                {o.images[0] && <img src={o.images[0]} alt="" style={{ width: "100%", height: 96, objectFit: "cover", borderRadius: 6, marginBottom: 7, display: "block" }} />}
                <div className="row" style={{ gap: 6, alignItems: "baseline" }}>
                  <span className="badge accent mono" style={{ fontSize: 9 }}>{o.ref}</span>
                  <span className="serif" style={{ fontSize: 16 }}>{o.name}</span>
                </div>
                {o.response?.choice === "preferred" && <div style={{ fontSize: 10.5, color: "var(--ok)", marginTop: 3, textTransform: "none", letterSpacing: 0 }}>Your choice</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <td style={{ fontWeight: 600, fontSize: 12.5, verticalAlign: "top", color: "var(--ink-2)" }}>{r.label}</td>
              {os.map((o) => <td key={o.id} style={{ fontSize: 13, verticalAlign: "top", lineHeight: 1.6 }}>{r.get(o)}</td>)}
            </tr>
          ))}
          <tr>
            <td />
            {os.map((o) => (
              <td key={o.id} style={{ verticalAlign: "top" }}>
                {o.response?.choice === "preferred"
                  ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />Chosen</span>
                  : <button className="btn accent sm" style={{ fontSize: 11.5, padding: "5px 10px" }} onClick={() => actions.respondToOption(p.id, o.id, "preferred", who)}>Choose this</button>}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ---- a comment against one slide ---- */
function SlideComment({ p, slideId, who }: { p: Project; slideId: string; who: string }) {
  const notes = (p.deck?.notes ?? []).filter((n) => n.slideId === slideId);
  const [open, setOpen] = useState(false);
  const [v, setV] = useState("");

  return (
    <div style={{ marginTop: notes.length || open ? 13 : 10 }}>
      {notes.map((n) => (
        <div key={n.id} className="panel" style={{ padding: "9px 12px", marginBottom: 6 }}>
          <div className="row between"><b style={{ fontSize: 12.5 }}>{n.by}</b><span className="dim" style={{ fontSize: 11 }}>{relative(n.on)}</span></div>
          <div style={{ fontSize: 13, marginTop: 3, lineHeight: 1.55 }}>{n.body}</div>
          {n.reply && (
            <div style={{ marginTop: 8, paddingLeft: 11, borderLeft: "2px solid var(--accent)" }}>
              <div className="kicker" style={{ fontSize: 8 }}>{STUDIO.name}</div>
              <div style={{ fontSize: 13, marginTop: 2, lineHeight: 1.55 }}>{n.reply.body}</div>
            </div>
          )}
        </div>
      ))}
      {open ? (
        <div className="row" style={{ gap: 8 }}>
          <input className="input" style={{ fontSize: 13 }} autoFocus value={v} onChange={(e) => setV(e.target.value)} placeholder="Your thoughts on this…"
            onKeyDown={(e) => { if (e.key === "Enter" && v.trim()) { actions.addDeckNote(p.id, { slideId, body: v.trim(), by: who, author: "client" }); setV(""); setOpen(false); } }} />
          <button className="btn accent sm" disabled={!v.trim()} onClick={() => { actions.addDeckNote(p.id, { slideId, body: v.trim(), by: who, author: "client" }); setV(""); setOpen(false); }}><Icon.send width={13} /></button>
          <button className="btn ghost sm" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      ) : (
        <button className="chip" style={{ fontSize: 11 }} onClick={() => setOpen(true)}><Icon.chat width={11} />{notes.length ? "Add another comment" : "Comment on this"}</button>
      )}
    </div>
  );
}
